import { SHARE_DOMAIN_LABEL } from "../config/lumacofono";
import { warn } from "./log";
import { ROLES } from "../game/roles";
import { ROLE_COLORS } from "../components/game/roleIcons";
import { CHALLENGE_TITLES } from "./challenge";

// Draws the 1080x1920 (9:16) story version of the Result Card.
const W = 1080;
const H = 1920;
const DISPLAY = "Fredoka, sans-serif";
const BODY = '"Plus Jakarta Sans", sans-serif';

const rr = (ctx, x, y, w, h, r) => {
  ctx.beginPath();
  ctx.roundRect(x, y, w, h, r);
};

const spaced = (ctx, px) => {
  if ("letterSpacing" in ctx) ctx.letterSpacing = `${px}px`;
};

const fit = (ctx, text, max) => {
  if (ctx.measureText(text).width <= max) return text;
  let t = text;
  while (t.length > 1 && ctx.measureText(`${t}…`).width > max) t = t.slice(0, -1);
  return `${t}…`;
};

const wrap = (ctx, text, max) => {
  const lines = [];
  let line = "";
  text.split(" ").forEach((word) => {
    const test = line ? `${line} ${word}` : word;
    if (ctx.measureText(test).width > max && line) {
      lines.push(line);
      line = word;
    } else line = test;
  });
  lines.push(line);
  return lines;
};

const blob = (ctx, x, y, r, inner, outer) => {
  const g = ctx.createRadialGradient(x, y, 0, x, y, r);
  g.addColorStop(0, inner);
  g.addColorStop(1, outer);
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, W, H);
};

const GOLD = "#FFD166";
const CARD = { x: 90, w: 900, band: 88, pillH: 58, pillGap: 8, radius: 64 };
const ISLAND_MAX_W = 720;

const gradientStops = (g, colors) => colors.forEach((c, i) => g.addColorStop(i / (colors.length - 1), c));

const loadFonts = () =>
  Promise.all([
    document.fonts.load(`700 120px ${DISPLAY}`),
    document.fonts.load(`800 26px ${BODY}`),
    document.fonts.load(`700 34px ${BODY}`),
  ]).catch((e) => warn("Font non caricati per l'immagine", e));

const drawBackground = (ctx, lt) => {
  ctx.fillStyle = "#151526";
  ctx.fillRect(0, 0, W, H);
  blob(ctx, 960, 180, 620, lt ? "rgba(255,209,102,0.55)" : "rgba(242,109,181,0.55)", "rgba(21,21,38,0)");
  blob(ctx, 80, 1500, 640, lt ? "rgba(242,109,181,0.45)" : "rgba(85,215,243,0.42)", "rgba(21,21,38,0)");
  blob(ctx, 600, 1000, 700, "rgba(118,85,232,0.28)", "rgba(21,21,38,0)");
};

// Picks the largest font size that fits the island name in max 2 lines.
const fitIsland = (ctx, island) => {
  const text = island.toUpperCase();
  let fs = 116;
  const measure = () => {
    ctx.font = `700 ${fs}px ${DISPLAY}`;
    return wrap(ctx, text, ISLAND_MAX_W);
  };
  let lines = measure();
  while ((lines.length > 2 || lines.some((l) => ctx.measureText(l).width > ISLAND_MAX_W)) && fs > 70) {
    fs -= 8;
    lines = measure();
  }
  return { fs, lines };
};

const QUOTE_FONT = `700 30px ${BODY}`;
const NAME_FONT = `700 60px ${DISPLAY}`;

const wrapWith = (ctx, font, text, max) => {
  ctx.font = font;
  return text ? wrap(ctx, text, max) : [];
};

const titleHeight = (nameLines) => (nameLines.length ? nameLines.length * 62 + 10 + 36 : 64);

const computeLayout = (island, lt, nameLines, quoteLines, hasChallenge) => {
  const quoteH = quoteLines.length ? quoteLines.length * 40 + 18 : 0;
  const boxH = 40 + 30 + 22 + (lt ? 52 : 0) + island.lines.length * island.fs * 1.02 + quoteH + 30 + 72 + 42;
  const rolesH = ROLES.length * CARD.pillH + (ROLES.length - 1) * CARD.pillGap;
  const cardH = CARD.band + 44 + titleHeight(nameLines) + 24 + rolesH + 40 + boxH + 30 + 34 + 42;
  const extra = hasChallenge ? CHALLENGE_BAND.h + CHALLENGE_BAND.gap : 0;
  return { boxH, cardH, cardY: Math.round((H - cardH - extra) / 2) + extra };
};

const CHALLENGE_BAND = { h: 124, gap: 34 };
const CHALLENGE_COLORS = { win: "#55D7F3", draw: "#EEE9FA", lose: "#F26DB5" };

// Outcome band above the card, only for games started from a friend's challenge link.
const drawChallengeBand = (ctx, { outcome, island }, cardY) => {
  const y = cardY - CHALLENGE_BAND.gap - CHALLENGE_BAND.h;
  rr(ctx, CARD.x, y, CARD.w, CHALLENGE_BAND.h, 62);
  ctx.fillStyle = CHALLENGE_COLORS[outcome];
  ctx.fill();
  ctx.fillStyle = "#151526";
  ctx.textAlign = "center";
  ctx.textBaseline = "top";
  ctx.font = `700 50px ${DISPLAY}`;
  spaced(ctx, 2);
  ctx.fillText(CHALLENGE_TITLES[outcome], W / 2, y + 16);
  spaced(ctx, 0);
  ctx.font = `700 28px ${BODY}`;
  ctx.fillText(fit(ctx, `Isola dell'amico: ${island}`, CARD.w - 80), W / 2, y + 76);
};

const drawCardBody = (ctx, { cardY, cardH }, lt) => {
  const { x, w, radius } = CARD;
  if (lt) {
    ctx.save();
    ctx.shadowColor = "rgba(255,209,102,0.7)";
    ctx.shadowBlur = 70;
    rr(ctx, x, cardY, w, cardH, radius);
    ctx.fillStyle = "#F8F7FC";
    ctx.fill();
    ctx.restore();
  }
  rr(ctx, x, cardY + 22, w, cardH, radius);
  ctx.fillStyle = lt ? "#E0A83A" : "#7655E8";
  ctx.fill();
  rr(ctx, x, cardY, w, cardH, radius);
  ctx.fillStyle = "#F8F7FC";
  ctx.fill();
  if (lt) {
    ctx.lineWidth = 6;
    ctx.strokeStyle = GOLD;
    ctx.stroke();
  }
};

const drawBand = (ctx, { cardY, cardH }, lt) => {
  const { x, w, band, radius } = CARD;
  ctx.save();
  rr(ctx, x, cardY, w, cardH, radius);
  ctx.clip();
  const bg = ctx.createLinearGradient(x, 0, x + w, 0);
  gradientStops(bg, lt ? [GOLD, "#F26DB5", "#7655E8"] : ["#55D7F3", "#7655E8", "#F26DB5"]);
  ctx.fillStyle = bg;
  ctx.fillRect(x, cardY, w, band);
  ctx.restore();
  ctx.fillStyle = "#FFFFFF";
  ctx.font = `800 28px ${BODY}`;
  spaced(ctx, 5);
  ctx.textBaseline = "middle";
  ctx.textAlign = "left";
  ctx.fillText("COSTRUISCI LA TUA CIURMA", x + 56, cardY + band / 2);
  spaced(ctx, 0);
  [["rgba(255,255,255,0.9)", 0], [GOLD, 1]].forEach(([c, i]) => {
    ctx.beginPath();
    ctx.arc(x + w - 92 + i * 34, cardY + band / 2, 11, 0, Math.PI * 2);
    ctx.fillStyle = c;
    ctx.fill();
  });
};

const drawTitle = (ctx, y, nameLines) => {
  ctx.textBaseline = "top";
  ctx.textAlign = "left";
  if (!nameLines.length) {
    ctx.fillStyle = "#151526";
    ctx.font = `700 64px ${DISPLAY}`;
    ctx.fillText("LA TUA CIURMA", CARD.x + 56, y);
    return y + 64 + 24;
  }
  let cy = y;
  ctx.font = NAME_FONT;
  nameLines.forEach((line) => {
    const g = ctx.createLinearGradient(CARD.x + 56, 0, CARD.x + 56 + ctx.measureText(line).width, 0);
    gradientStops(g, ["#7655E8", "#F26DB5"]);
    ctx.fillStyle = g;
    ctx.fillText(fit(ctx, line, 780), CARD.x + 56, cy);
    cy += 62;
  });
  ctx.fillStyle = "#151526";
  ctx.font = `700 36px ${DISPLAY}`;
  ctx.fillText("LA TUA CIURMA", CARD.x + 56, cy + 10);
  return y + titleHeight(nameLines) + 24;
};

const drawRolePill = (ctx, role, name, y) => {
  const { pillH } = CARD;
  const px = CARD.x + 48;
  const pw = CARD.w - 96;
  rr(ctx, px, y, pw, pillH, pillH / 2);
  ctx.fillStyle = "#EEE9FA";
  ctx.fill();
  ctx.beginPath();
  ctx.arc(px + 40, y + pillH / 2, 10, 0, Math.PI * 2);
  ctx.fillStyle = ROLE_COLORS[role.key];
  ctx.fill();
  ctx.textBaseline = "middle";
  ctx.font = `800 24px ${BODY}`;
  spaced(ctx, 3);
  ctx.fillStyle = "#5B4F86";
  ctx.textAlign = "left";
  const label = role.label.toUpperCase();
  ctx.fillText(label, px + 68, y + pillH / 2 + 1);
  const labelW = ctx.measureText(label).width;
  spaced(ctx, 0);
  ctx.font = `700 32px ${BODY}`;
  ctx.fillStyle = "#151526";
  ctx.textAlign = "right";
  ctx.fillText(fit(ctx, name, pw - 68 - labelW - 60), px + pw - 32, y + pillH / 2 + 1);
  ctx.textAlign = "left";
};

const drawRoles = (ctx, assignments, y) => {
  let cy = y;
  ROLES.forEach((r) => {
    drawRolePill(ctx, r, assignments[r.key]?.name || "", cy);
    cy += CARD.pillH + CARD.pillGap;
  });
  return cy + 40 - CARD.pillGap;
};

const drawIslandName = (ctx, { fs, lines }, iy, lt) => {
  const cx = W / 2;
  let y = iy;
  spaced(ctx, 0);
  ctx.font = `700 ${fs}px ${DISPLAY}`;
  if (lt) {
    ctx.shadowColor = "rgba(255,209,102,0.6)";
    ctx.shadowBlur = 40;
  }
  lines.forEach((line) => {
    const lw = ctx.measureText(line).width;
    const g = ctx.createLinearGradient(cx - lw / 2, 0, cx + lw / 2, 0);
    gradientStops(g, lt ? [GOLD, "#F26DB5", "#A989FF"] : ["#55D7F3", "#A989FF", "#F26DB5"]);
    ctx.fillStyle = g;
    ctx.fillText(line, cx, y);
    y += fs * 1.02;
  });
  ctx.shadowBlur = 0;
  return y;
};

const drawChallenge = (ctx, iy) => {
  const cx = W / 2;
  ctx.font = `700 34px ${DISPLAY}`;
  spaced(ctx, 3);
  const text = "RIESCI A BATTERMI?";
  const cw = ctx.measureText(text).width + 80;
  rr(ctx, cx - cw / 2, iy, cw, 72, 36);
  ctx.fillStyle = GOLD;
  ctx.fill();
  ctx.fillStyle = "#151526";
  ctx.textBaseline = "middle";
  ctx.fillText(text, cx, iy + 37);
  spaced(ctx, 0);
};

const drawQuote = (ctx, quoteLines, iy) => {
  if (!quoteLines.length) return iy;
  let y = iy + 18;
  ctx.font = QUOTE_FONT;
  ctx.fillStyle = "#EEE9FA";
  ctx.textBaseline = "top";
  quoteLines.forEach((line) => {
    ctx.fillText(line, W / 2, y);
    y += 40;
  });
  return y;
};

const drawIslandBox = (ctx, island, y, boxH, lt, quoteLines) => {
  const cx = W / 2;
  rr(ctx, CARD.x + 48, y, CARD.w - 96, boxH, 52);
  ctx.fillStyle = "#151526";
  ctx.fill();
  if (lt) {
    ctx.lineWidth = 4;
    ctx.strokeStyle = GOLD;
    ctx.stroke();
  }
  let iy = y + 40;
  ctx.textAlign = "center";
  ctx.textBaseline = "top";
  ctx.font = `800 26px ${BODY}`;
  spaced(ctx, 5);
  ctx.fillStyle = "#55D7F3";
  ctx.fillText("SEI ARRIVATO FINO A:", cx, iy);
  iy += 30 + 22;
  if (lt) {
    ctx.font = `800 24px ${BODY}`;
    ctx.fillStyle = GOLD;
    ctx.fillText("HAI RAGGIUNTO LA FINE DEL VIAGGIO", cx, iy);
    iy += 52;
  }
  iy = drawIslandName(ctx, island, iy, lt);
  iy = drawQuote(ctx, quoteLines, iy);
  drawChallenge(ctx, iy + 30);
};

const drawFooter = (ctx, y) => {
  ctx.font = `700 30px ${BODY}`;
  ctx.fillStyle = "#9A90BF";
  ctx.fillText(SHARE_DOMAIN_LABEL, W / 2, y);
};

const toPngBlob = (canvas) =>
  new Promise((resolve, reject) =>
    canvas.toBlob((b) => (b ? resolve(b) : reject(new Error("toBlob failed"))), "image/png"),
  );

export async function renderResultImage({ assignments, island, isLaughTale, crewName = "", quote = "", challenge = null }) {
  await loadFonts();
  const canvas = document.createElement("canvas");
  canvas.width = W;
  canvas.height = H;
  const ctx = canvas.getContext("2d");

  drawBackground(ctx, isLaughTale);
  const fitted = fitIsland(ctx, island);
  const nameLines = wrapWith(ctx, NAME_FONT, crewName.toUpperCase(), 780).slice(0, 2);
  const quoteLines = wrapWith(ctx, QUOTE_FONT, quote ? `“${quote}”` : "", 700);
  const layout = computeLayout(fitted, isLaughTale, nameLines, quoteLines, Boolean(challenge));
  if (challenge) drawChallengeBand(ctx, challenge, layout.cardY);
  drawCardBody(ctx, layout, isLaughTale);
  drawBand(ctx, layout, isLaughTale);
  let y = drawTitle(ctx, layout.cardY + CARD.band + 44, nameLines);
  y = drawRoles(ctx, assignments, y);
  drawIslandBox(ctx, fitted, y, layout.boxH, isLaughTale, quoteLines);
  drawFooter(ctx, y + layout.boxH + 30 + 17);
  return toPngBlob(canvas);
}
